import { EXPLORER_CURRENCY, FX_RATES_AS_OF } from '@/lib/fxToUsd';
import { useMemo } from 'react';
import { Check, ChevronsUpDown, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { LComponents } from '@/components/i18n';
import { useLocalizations } from '@/hooks/useLocalizations';
import { useLocalizedLocations } from '@/hooks/useLocalizedLocations';
import { useLocale } from '@/contexts/LocaleContext';
import { cn } from '@/lib/utils';
import { INCOME_MAX, INCOME_MIN, NET_WORTH_MAX } from '@/lib/exploreScenarioUrl';
import type { LocationWithCoordinates, UserScenario } from '@/types/taxExplorer';

interface ScenarioPanelProps {
  scenario: UserScenario;
  locations: LocationWithCoordinates[];
  currentTax: number;
  onScenarioChange: (updates: Partial<UserScenario>) => void;
  onReset: () => void;
}

const INCOME_TYPES: { id: UserScenario['incomeType']; key: string; dv: string }[] = [
  { id: 'employment', key: 'tax_explorer.earned_as.salary', dv: 'Salary' },
  { id: 'self-employed', key: 'tax_explorer.earned_as.self_employed', dv: 'Self-employed' },
  { id: 'investments', key: 'tax_explorer.earned_as.investments', dv: 'Investments' },
  { id: 'pension', key: 'tax_explorer.earned_as.pension', dv: 'Pension' },
  { id: 'mixed', key: 'tax_explorer.earned_as.mixed', dv: 'Mixed' },
];

const WISHES: { id: string; key: string; dv: string }[] = [
  { id: 'eu-access', key: 'tax_explorer.wishes.eu_access', dv: 'EU access' },
  { id: 'digital-nomad', key: 'tax_explorer.wishes.digital_nomad', dv: 'Nomad visa' },
  { id: 'crypto-friendly', key: 'tax_explorer.wishes.crypto_friendly', dv: 'Crypto-friendly' },
  { id: 'english-speaking', key: 'tax_explorer.wishes.english_speaking', dv: 'English-speaking' },
];

function flag(code: string | null): string {
  if (!code || code.length !== 2) return '🌐';
  return String.fromCodePoint(...code.toUpperCase().split('').map((c) => 127397 + c.charCodeAt(0)));
}

export function ScenarioPanel({ scenario, locations, currentTax, onScenarioChange, onReset }: ScenarioPanelProps): JSX.Element {
  const { lComponents } = useLocalizations();
  const { formatCompactCurrency } = useLocale();
  const { locations: localized } = useLocalizedLocations();

  const items = useMemo(() => {
    const nameByIso = new Map(localized.map((l) => [(l.iso ?? '').toUpperCase(), l.name]));
    return locations
      .filter((l) => l.iso_alpha2)
      .map((l) => ({ id: l.location_id, iso: l.iso_alpha2, name: nameByIso.get((l.iso_alpha2 ?? '').toUpperCase()) || l.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [locations, localized]);
  const current = items.find((i) => i.id === scenario.currentResidencyId);

  const toggleWish = (id: string): void => {
    const has = scenario.lifestylePrefs.includes(id);
    onScenarioChange({ lifestylePrefs: has ? scenario.lifestylePrefs.filter((w) => w !== id) : [...scenario.lifestylePrefs, id] });
  };

  return (
    <div className="flex flex-col gap-5">
      <h2 className="text-base font-semibold text-foreground">
        <LComponents id="tax_explorer.panel.title" defaultValue="Your situation" />
      </h2>

      {/* Live in */}
      <div className="flex flex-col gap-2">
        <Label><LComponents id="tax_explorer.live_in.label" defaultValue="Live in" /></Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" role="combobox" className="justify-between font-normal">
              <span className="flex items-center gap-2 truncate">
                {current ? <><span aria-hidden="true">{flag(current.iso)}</span>{current.name}</>
                  : <span className="text-muted-foreground"><LComponents id="tax_explorer.live_in.placeholder" defaultValue="Choose a country" /></span>}
              </span>
              <ChevronsUpDown className="h-4 w-4 opacity-60" aria-hidden="true" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start">
            <Command>
              <CommandInput placeholder={lComponents('tax_explorer.live_in.search', { defaultValue: 'Search countries' })} />
              <CommandList className="max-h-72">
                <CommandEmpty><LComponents id="tax_explorer.live_in.empty" defaultValue="No country found" /></CommandEmpty>
                <CommandGroup>
                  {items.map((i) => (
                    <CommandItem key={i.id} value={`${i.name} ${i.iso ?? ''}`} onSelect={() => onScenarioChange({ currentResidencyId: i.id })}>
                      <span className="me-2" aria-hidden="true">{flag(i.iso)}</span>
                      <span className="flex-1">{i.name}</span>
                      {i.id === scenario.currentResidencyId && <Check className="h-4 w-4" aria-hidden="true" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Income */}
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between">
          <Label id="tx-income"><LComponents id="tax_explorer.income" defaultValue="Yearly income" /></Label>
          <span className="text-sm font-semibold text-foreground">{formatCompactCurrency(scenario.grossIncome, EXPLORER_CURRENCY)}</span>
        </div>
        <Slider aria-labelledby="tx-income" value={[scenario.grossIncome]} min={INCOME_MIN} max={INCOME_MAX} step={10000}
          onValueChange={([v]) => onScenarioChange({ grossIncome: v })} />
      </div>

      {/* Earned as */}
      <div className="flex flex-col gap-2">
        <Label><LComponents id="tax_explorer.earned_as.label" defaultValue="Earned as" /></Label>
        <div className="flex flex-wrap gap-2" role="radiogroup">
          {INCOME_TYPES.map((t) => (
            <Button key={t.id} type="button" size="sm" role="radio" aria-checked={scenario.incomeType === t.id}
              variant={scenario.incomeType === t.id ? 'default' : 'outline'}
              onClick={() => onScenarioChange({ incomeType: t.id })}>
              <LComponents id={t.key} defaultValue={t.dv} />
            </Button>
          ))}
        </div>
      </div>

      {/* Net worth */}
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between">
          <Label id="tx-worth"><LComponents id="tax_explorer.net_worth" defaultValue="Net worth" /></Label>
          <span className="text-sm font-semibold text-foreground">{formatCompactCurrency(scenario.netWorth, EXPLORER_CURRENCY)}</span>
        </div>
        <Slider aria-labelledby="tx-worth" value={[scenario.netWorth]} min={0} max={NET_WORTH_MAX} step={50000}
          onValueChange={([v]) => onScenarioChange({ netWorth: v })} />
      </div>

      {/* Days */}
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between">
          <Label id="tx-days"><LComponents id="tax_explorer.days" defaultValue="Days per year in the new country" /></Label>
          <span className="text-sm font-semibold text-foreground">{scenario.plannedDays}</span>
        </div>
        <Slider aria-labelledby="tx-days" value={[scenario.plannedDays]} min={0} max={365} step={1}
          onValueChange={([v]) => onScenarioChange({ plannedDays: v })} />
      </div>

      {/* Wishes */}
      <div className="flex flex-col gap-2">
        <Label><LComponents id="tax_explorer.wishes.label" defaultValue="Nice to have" /></Label>
        <div className="flex flex-wrap gap-2">
          {WISHES.map((w) => {
            const on = scenario.lifestylePrefs.includes(w.id);
            return (
              <Button key={w.id} type="button" size="sm" aria-pressed={on} variant={on ? 'secondary' : 'ghost'}
                className="rounded-full border border-border" onClick={() => toggleWish(w.id)}>
                <LComponents id={w.key} defaultValue={w.dv} />
              </Button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-border pt-4">
        <div className="text-sm">
          <div className="text-muted-foreground"><LComponents id="tax_explorer.current_tax" defaultValue="Tax where you live now" /></div>
          <div className={cn('text-lg font-semibold', current ? 'text-foreground' : 'text-muted-foreground')}>
            {current ? formatCompactCurrency(currentTax, EXPLORER_CURRENCY) : '—'}
          </div>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onReset}>
          <RotateCcw className="h-4 w-4 me-1" aria-hidden="true" />
          <LComponents id="tax_explorer.reset" defaultValue="Reset" />
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        <LComponents id="tax_explorer.currency_note" defaultValue="Amounts in US dollars. Estimates only. Rates as of {{date}}." values={{ date: FX_RATES_AS_OF }} />
      </p>
    </div>
  );
}
